import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504])
const RETRYABLE_METHODS = new Set(['get', 'head', 'options'])
const MAX_RETRY_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 500
const RETRY_MAX_DELAY_MS = 4000

const baseURL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  'http://localhost:8787'

const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
})

export function setAuthToken(token: string | null) {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`
  } else {
    delete apiClient.defaults.headers.common.Authorization
  }
}

function getRetryAttempt(config: AxiosRequestConfig): number {
  const retryAttempt = (config as AxiosRequestConfig & { __retryAttempt?: number }).__retryAttempt
  return Number.isFinite(retryAttempt) ? Number(retryAttempt) : 0
}

function setRetryAttempt(config: AxiosRequestConfig, attempt: number): void {
  ;(config as AxiosRequestConfig & { __retryAttempt?: number }).__retryAttempt = attempt
}

function isRetryableRequest(config?: AxiosRequestConfig): boolean {
  const method = String(config?.method || 'get').toLowerCase()
  return RETRYABLE_METHODS.has(method)
}

function isRetryableError(error: AxiosError): boolean {
  if (error.code === 'ERR_CANCELED') return false
  if (!error.response) return true
  return RETRYABLE_STATUS_CODES.has(error.response.status)
}

async function delay(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

function calculateBackoffMs(attempt: number): number {
  const exponential = RETRY_BASE_DELAY_MS * Math.pow(2, Math.max(0, attempt - 1))
  const jitterFactor = 0.85 + Math.random() * 0.3
  return Math.min(RETRY_MAX_DELAY_MS, Math.round(exponential * jitterFactor))
}

apiClient.interceptors.request.use((config) => {
  const token = window.localStorage.getItem('nz_tax_auth_token')
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config
    if (!config || !isRetryableRequest(config) || !isRetryableError(error)) {
      return Promise.reject(error)
    }

    const attempt = getRetryAttempt(config)
    if (attempt >= MAX_RETRY_ATTEMPTS) {
      return Promise.reject(error)
    }

    const nextAttempt = attempt + 1
    setRetryAttempt(config, nextAttempt)
    await delay(calculateBackoffMs(nextAttempt))
    return apiClient(config)
  },
)

export default apiClient
